import { Component, OnInit, ViewChild } from '@angular/core';
import { GridComponent } from 'src/app/core/widgets/grid/grid.component';
import { GridColumn } from 'src/app/core/models/grid-column.model';
import { GridFilter } from 'src/app/core/models/grid-filter.model';
import { GridSearchFilter } from 'src/app/core/models/grid-search-filter.model';
import { take, catchError } from 'rxjs/operators';
import moment from 'moment';
import { Product } from 'src/app/order/models/product.model';
import { ProductService } from 'src/app/quoting/services/product.service';
import { Warehouse } from '../../models/warehouse.model';
import { WarehouseLocation } from '../../models/warehouse-location.model';
import { WarehouseService } from '../../services/warehouse.service';
import { WarehouseLocationService } from '../../services/warehouse-location.service';
import { StockQuantityService } from '../../services/stock-quantity.service';
import { StockWeightService } from '../../services/stock-weight.service';
import { MOVEMENT_TYPE_LABELS } from '../../models/product-stock-movement.model';

@Component({
    selector: 'app-stock-movements',
    templateUrl: './stock-movements.component.html',
    styleUrls: ['./stock-movements.component.scss'],
    standalone: false
})
export class StockMovementsComponent implements OnInit {

  // Grid reads this.filters when it queries but has no ngOnChanges, so changing
  // a selection has to ask it to re-query.
  @ViewChild(GridComponent)
  grid: GridComponent;

  warehouses: Warehouse[] = [];
  locations: WarehouseLocation[] = [];
  products: Product[] = [];

  selectedWarehouseId: number = -1;
  selectedLocationId: number = -1;
  selectedProductId: number = -1;
  selectedMovementType: string = '';

  movementTypes: any[] = MOVEMENT_TYPE_LABELS;

  // The ledger is append only, so this grid carries no row buttons.
  columns: GridColumn[] = [
    {active: true, label: '#', field: 'id'} as GridColumn,
    {active: true, label: 'Date', field: 'createdAt', value: v => moment(v).format('DD/MM/YYYY HH:mm:ss')} as GridColumn,
    {active: true, label: 'Product', field: 'product.name'} as GridColumn,
    {active: true, label: 'SKU', field: 'product.sku'} as GridColumn,
    {active: true, label: 'Warehouse', field: 'warehouseLocation.warehouse.name'} as GridColumn,
    {active: true, label: 'Aisle', field: 'warehouseLocation.aisle'} as GridColumn,
    {active: true, label: 'Shelf', field: 'warehouseLocation.shelf'} as GridColumn,
    {active: true, label: 'Type', field: 'movementType', value: v => this.getMovementTypeLabel(v)} as GridColumn,
    // Grid blanks a falsy value, so the qty is rendered as a string.
    {active: true, label: 'Qty', field: 'qty', value: v => this.stockQuantityService.round(v).toString()} as GridColumn,
    // Blank on most rows, same as batch: neither is required, and this is the raw ledger, not a
    // filtered report, so no coverage note belongs beside them here.
    {active: true, label: 'Batch', field: 'batchNumber'} as GridColumn,
    {active: true, label: 'Start Weight', field: 'startWeight', value: v => this.stockWeightService.format(v)} as GridColumn,
    {active: true, label: 'End Weight', field: 'endWeight', value: v => this.stockWeightService.format(v)} as GridColumn,
    // Computed, so it reads both fields off the row rather than one column of its own on the table.
    {active: true, label: 'Net Weight', field: 'id', value: (v, record) => this.stockWeightService.formatNet(record)} as GridColumn,
    {active: true, label: 'Reference', field: 'reference.name'} as GridColumn,
    {active: true, label: 'Reference Code', field: 'reference.code'} as GridColumn
  ];

  relationships: string[] = ['product', 'warehouseLocation', 'warehouseLocation.warehouse', 'reference'];

  filters: GridFilter[] = []

  searchFields: any = ['product.name','product.sku','reference.name','reference.code','warehouseLocation.aisle','warehouseLocation.shelf','warehouseLocation.warehouse.name'];

  searchFilters: GridSearchFilter[] = [
    {
      field: 'product.name',
      label: 'Product',
    } as GridSearchFilter,
    {
      field: 'product.sku',
      label: 'SKU',
    } as GridSearchFilter,
    {
      field: 'reference.name',
      label: 'Reference',
    } as GridSearchFilter,
    {
      field: 'reference.code',
      label: 'Reference Code',
    } as GridSearchFilter,
    {
      field: 'warehouseLocation.warehouse.name',
      label: 'Warehouse',
    } as GridSearchFilter,
    {
      field: 'warehouseLocation.aisle',
      label: 'Aisle',
    } as GridSearchFilter,
    {
      field: 'warehouseLocation.shelf',
      label: 'Shelf',
    } as GridSearchFilter,
  ]

  constructor(private warehouseService: WarehouseService,
    private warehouseLocationService: WarehouseLocationService,
    private productService: ProductService,
    private stockQuantityService: StockQuantityService,
    private stockWeightService: StockWeightService) { }

  ngOnInit(): void {
    this.loadWarehouses();
    this.loadProducts();
  }

  getMovementTypeLabel(movementType: string): string {
    const type = this.movementTypes.filter(t => t.value === movementType)[0];

    return type ? type.label : movementType;
  }

  loadWarehouses() {
    this.warehouseService.getAllWarehouses()
    .pipe(take(1))
    .pipe(catchError((e) => {
      if (e.status === 403 || e.status === 401) {
        return e;
      }
      alert("Cannot load warehouses");
      return e;
    }))
    .subscribe((data: Warehouse[]) => {
      this.warehouses = data;
    })
  }

  loadProducts() {
    this.productService.getAllActiveProducts()
    .pipe(take(1))
    .pipe(catchError((e) => {
      if (e.status === 403 || e.status === 401) {
        return e;
      }
      alert("Cannot load products");
      return e;
    }))
    .subscribe((data: Product[]) => {
      this.products = data;
    })
  }

  loadLocations() {
    this.warehouseLocationService.getWarehouseLocationsForWarehouse(this.selectedWarehouseId)
    .pipe(take(1))
    .pipe(catchError((e) => {
      if (e.status === 403 || e.status === 401) {
        return e;
      }
      alert("Cannot load warehouse locations");
      return e;
    }))
    .subscribe((data: WarehouseLocation[]) => {
      this.locations = data;
    })
  }

  onWarehouseChanged(warehouseId: number) {
    this.selectedWarehouseId = +warehouseId;

    // The location list is scoped to the warehouse, so a location picked at the old
    // site no longer applies.
    this.selectedLocationId = -1;
    this.locations = [];

    if (this.selectedWarehouseId !== -1) {
      this.loadLocations();
    }

    this.applyFilters();
  }

  onLocationChanged(locationId: number) {
    this.selectedLocationId = +locationId;
    this.applyFilters();
  }

  onProductChanged(productId: number) {
    this.selectedProductId = +productId;
    this.applyFilters();
  }

  onMovementTypeChanged(movementType: string) {
    this.selectedMovementType = movementType;
    this.applyFilters();
  }

  /**
   * A location already belongs to one warehouse, so filtering on the location alone is
   * enough once one is picked.
   */
  private applyFilters() {
    const filters: GridFilter[] = [];

    if (this.selectedLocationId !== -1) {
      filters.push({field: 'warehouseLocationId', condition: 'eq', value: this.selectedLocationId} as GridFilter);
    } else if (this.selectedWarehouseId !== -1) {
      filters.push({field: 'warehouseLocation.warehouseId', condition: 'eq', value: this.selectedWarehouseId} as GridFilter);
    }

    if (this.selectedProductId !== -1) {
      filters.push({field: 'productId', condition: 'eq', value: this.selectedProductId} as GridFilter);
    }

    if (this.selectedMovementType !== '') {
      filters.push({field: 'movementType', condition: 'eq', value: this.selectedMovementType} as GridFilter);
    }

    this.filters = filters;

    this.reloadGrid();
  }

  private reloadGrid() {
    if (!this.grid) {
      return;
    }

    this.grid.filters = this.filters;
    this.grid.paginationService.setPage(1);
    this.grid.newPage = 1;
    this.grid.getRecordsForEntity();
  }
}
